
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, UserRole } from '../../types';
import StatusBadge from './StatusBadge';
import { Timestamp } from 'firebase/firestore';

interface VideoCardProps {
  video: Video;
  basePath?: string;
  userRole?: UserRole | null;
  onDelete?: (video: Video) => void;
}

const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg>;
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;


const VideoCard: React.FC<VideoCardProps> = ({ video, basePath = '/videos', userRole, onDelete }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`${basePath}/${video.videoId}`);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`${basePath}/${video.videoId}`);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(video);
    }
  };

  const formatDate = (date: any) => {
    if (date instanceof Timestamp) {
      return date.toDate().toLocaleDateString();
    }
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString();
    }
    return 'Invalid Date';
  }

  return (
    <div 
      className="bg-gray-900 rounded-lg overflow-hidden shadow-lg hover:shadow-blue-500/20 transition-all duration-300 ease-in-out transform hover:-translate-y-1 cursor-pointer relative"
      onClick={handleCardClick}
    >
      {/* Fixed: Replaced SUPER_ADMIN with PLATFORM_OWNER and COMPANY_ADMIN with TENANT_ADMIN */}
      {(userRole === UserRole.PLATFORM_OWNER || userRole === UserRole.TENANT_ADMIN) && (
        <div className="absolute top-2 left-2 flex gap-2 z-10">
          <button onClick={handleEdit} className="p-1.5 bg-gray-800/60 hover:bg-blue-600 rounded-full transition-colors" title="Edit Video"><EditIcon /></button>
          <button onClick={handleDelete} className="p-1.5 bg-gray-800/60 hover:bg-red-600 rounded-full transition-colors" title="Delete Video"><DeleteIcon /></button>
        </div>
      )}
      <div className="relative">
        <img className="w-full h-48 object-cover" src={video.thumbnailUrl} alt={video.title} />
        <div className="absolute top-2 right-2">
            <StatusBadge status={video.status} />
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-100 truncate">{video.title}</h3>
        <p className="text-gray-400 text-sm mt-1 h-10 overflow-hidden text-ellipsis">{video.description}</p>
        <p className="text-gray-500 text-xs mt-3">Uploaded: {formatDate(video.createdAt)}</p>
      </div>
    </div>
  );
};

export default VideoCard;
